/**
 * MMOOrbitalPlatform - MMO mode orbital platform entity
 * Displays orbital platforms on the hex grid with tier-specific visuals
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord, ISO_Y_SCALE } from '../../shared/HexUtils';
import type { PlatformTier } from '../../shared/common/MonetizationTypes';

const HEX_CELL_RADIUS = 42;

const TIER_COLORS: Record<PlatformTier, number> = {
  basic: 0x888888,
  advanced: 0x4488ff,
  elite: 0xffaa00,
  legendary: 0xaa44ff,
};

const TIER_LABELS: Record<PlatformTier, string> = {
  basic: 'Basic Platform',
  advanced: 'Advanced Platform',
  elite: 'Elite Platform',
  legendary: 'Legendary Platform',
};

export interface OrbitalPlatformData {
  id: string;
  userId: string | null;
  planetId: string;
  platformTier: PlatformTier;
  positionQ: number;
  positionR: number;
  currentHp: number;
  maxHp: number;
  currentShields: number;
  maxShields: number;
  mineralStorageCapacity: number;
  hasRepairBay: boolean;
  hasShipyard: boolean;
  hasUnitBuilding: boolean;
  hasOrbitalMining: boolean;
  isPublic: boolean;
  status: string;
}

export class MMOOrbitalPlatform extends Phaser.GameObjects.Container {
  public id: string;
  public userId: string | null;
  public planetId: string;
  public platformTier: PlatformTier;
  public hexCoord: HexCoord;
  public currentHp: number;
  public maxHp: number;
  public currentShields: number;
  public maxShields: number;
  public mineralStorageCapacity: number;
  public hasRepairBay: boolean;
  public hasShipyard: boolean;
  public hasUnitBuilding: boolean;
  public hasOrbitalMining: boolean;
  public isPublic: boolean;
  public status: string;

  private platformGraphics: Phaser.GameObjects.Graphics;
  private healthBar: Phaser.GameObjects.Graphics;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private glowGraphics: Phaser.GameObjects.Graphics;
  private glowTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, data: OrbitalPlatformData) {
    const pixel = axialToPixel(data.positionQ, data.positionR);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.userId = data.userId;
    this.planetId = data.planetId;
    this.platformTier = data.platformTier;
    this.hexCoord = { q: data.positionQ, r: data.positionR };
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.currentShields = data.currentShields;
    this.maxShields = data.maxShields;
    this.mineralStorageCapacity = data.mineralStorageCapacity;
    this.hasRepairBay = data.hasRepairBay;
    this.hasShipyard = data.hasShipyard;
    this.hasUnitBuilding = data.hasUnitBuilding;
    this.hasOrbitalMining = data.hasOrbitalMining;
    this.isPublic = data.isPublic;
    this.status = data.status;

    this.platformGraphics = this.scene.add.graphics();
    this.add(this.platformGraphics);

    this.healthBar = this.scene.add.graphics();
    this.healthBar.setVisible(false);
    this.add(this.healthBar);

    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    this.glowGraphics = this.scene.add.graphics();
    this.add(this.glowGraphics);
    this.sendToBack(this.glowGraphics);

    this.nameLabel = this.scene.add.text(0, 28, '', {
      fontSize: '10px',
      color: '#88ddff',
      fontFamily: '"Share Tech Mono", monospace',
    });
    this.nameLabel.setOrigin(0.5);
    this.nameLabel.setVisible(false);
    this.add(this.nameLabel);

    this.drawVisuals();
    this.setupGlowPulse();

    this.setDepth(7);

    const hitSize = 50;
    this.setSize(hitSize, hitSize);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-hitSize / 2, -hitSize / 2, hitSize, hitSize),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().
  }

  public updateFromServer(data: OrbitalPlatformData): void {
    if (!this.scene || !this.scene.sys) return;

    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.currentShields = data.currentShields;
    this.maxShields = data.maxShields;
    this.mineralStorageCapacity = data.mineralStorageCapacity;
    this.hasRepairBay = data.hasRepairBay;
    this.hasShipyard = data.hasShipyard;
    this.hasUnitBuilding = data.hasUnitBuilding;
    this.hasOrbitalMining = data.hasOrbitalMining;
    this.isPublic = data.isPublic;
    this.status = data.status;

    if (data.positionQ !== this.hexCoord.q || data.positionR !== this.hexCoord.r) {
      this.hexCoord = { q: data.positionQ, r: data.positionR };
      const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
      this.setPosition(pixel.x, pixel.y);
    }

    this.drawVisuals();
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
    this.healthBar.setVisible(selected || this.currentHp < this.maxHp);
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.35);
    } else {
      this.setAlpha(1);
    }
  }

  private drawVisuals(): void {
    this.drawPlatform();
    this.drawHealthBar();
    this.drawSelectionRing();
    this.updateLabels();
  }

  private getTierColor(): number {
    return TIER_COLORS[this.platformTier] || 0x888888;
  }

  private drawPlatform(): void {
    this.platformGraphics.clear();
    const color = this.getTierColor();
    const ringRadius = 20;
    const ringThickness = 5;

    // Outer hexagonal ring (smaller than hex cell)
    this.platformGraphics.lineStyle(2.5, color, 0.9);
    this.drawHexagon(this.platformGraphics, 0, 0, ringRadius + 2, false);
    this.platformGraphics.fillStyle(color, 0.3);
    this.drawHexagon(this.platformGraphics, 0, 0, ringRadius, true);

    // Inner cutout (darker center)
    this.platformGraphics.fillStyle(0x111122, 0.7);
    this.drawHexagon(this.platformGraphics, 0, 0, ringRadius - ringThickness, true);

    // Docking pylons — 3 arms extending outward at 120-degree intervals
    this.platformGraphics.lineStyle(2, color, 0.7);
    for (let i = 0; i < 3; i++) {
      const angle = (Math.PI * 2 / 3) * i - Math.PI / 2;
      const innerX = (ringRadius - 2) * Math.cos(angle);
      const innerY = (ringRadius - 2) * Math.sin(angle) * ISO_Y_SCALE;
      const outerX = (ringRadius + 10) * Math.cos(angle);
      const outerY = (ringRadius + 10) * Math.sin(angle) * ISO_Y_SCALE;

      this.platformGraphics.beginPath();
      this.platformGraphics.moveTo(innerX, innerY);
      this.platformGraphics.lineTo(outerX, outerY);
      this.platformGraphics.strokePath();

      // Small circle at the end of each pylon
      this.platformGraphics.fillStyle(color, 0.8);
      this.platformGraphics.fillCircle(outerX, outerY, 3);
    }

    // Center dot
    this.platformGraphics.fillStyle(color, 0.9);
    this.platformGraphics.fillCircle(0, 0, 3);

    // Public access indicator — small green circle at top-right
    if (this.isPublic) {
      this.platformGraphics.fillStyle(0x44ff44, 0.9);
      this.platformGraphics.fillCircle(14, -14 * ISO_Y_SCALE, 4);
      this.platformGraphics.lineStyle(1, 0x228822, 0.8);
      this.platformGraphics.strokeCircle(14, -14 * ISO_Y_SCALE, 4);
    }
  }

  private drawHealthBar(): void {
    this.healthBar.clear();
    const barWidth = 40;
    const barHeight = 4;
    const barY = 20;

    // Background
    this.healthBar.fillStyle(0x330000, 0.7);
    this.healthBar.fillRect(-barWidth / 2, barY, barWidth, barHeight);

    // HP fill
    const hpPct = this.maxHp > 0 ? this.currentHp / this.maxHp : 0;
    const hpColor = hpPct > 0.5 ? 0x00ff44 : hpPct > 0.25 ? 0xffff00 : 0xff3300;
    this.healthBar.fillStyle(hpColor, 0.9);
    this.healthBar.fillRect(-barWidth / 2, barY, barWidth * hpPct, barHeight);

    // Shield bar (thin line below HP bar)
    if (this.maxShields > 0) {
      const shieldY = barY + barHeight + 1;
      const shieldPct = this.currentShields / this.maxShields;

      this.healthBar.fillStyle(0x001133, 0.7);
      this.healthBar.fillRect(-barWidth / 2, shieldY, barWidth, 2);

      this.healthBar.fillStyle(0x4488ff, 0.8);
      this.healthBar.fillRect(-barWidth / 2, shieldY, barWidth * shieldPct, 2);
    }
  }

  private drawSelectionRing(): void {
    this.selectionIndicator.clear();
    const color = this.getTierColor();

    this.selectionIndicator.lineStyle(2, color, 0.8);
    this.drawHexagon(this.selectionIndicator, 0, 0, HEX_CELL_RADIUS + 2, false);
  }

  private updateLabels(): void {
    const tierLabel = TIER_LABELS[this.platformTier] || this.platformTier;
    const ownerSuffix = this.userId ? '' : ' (Earth)';
    this.nameLabel.setText(`${tierLabel}${ownerSuffix}`);

    const color = this.getTierColor();
    this.nameLabel.setColor(`#${color.toString(16).padStart(6, '0')}`);
  }

  private setupGlowPulse(): void {
    const color = this.getTierColor();

    this.glowGraphics.lineStyle(3, color, 0.3);
    this.drawHexagon(this.glowGraphics, 0, 0, 26, false);
    this.glowGraphics.setAlpha(0.3);

    this.glowTween = this.scene.tweens.add({
      targets: this.glowGraphics,
      alpha: { from: 0.2, to: 0.5 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  public destroy(fromScene?: boolean): void {
    if (this.glowTween) this.glowTween.destroy();
    if (this.glowGraphics) this.glowGraphics.destroy();
    super.destroy(fromScene);
  }

  private drawHexagon(
    graphics: Phaser.GameObjects.Graphics,
    cx: number, cy: number, size: number, fill: boolean,
  ): void {
    graphics.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const px = cx + size * Math.cos(angle);
      const py = cy + size * Math.sin(angle) * ISO_Y_SCALE;
      if (i === 0) graphics.moveTo(px, py);
      else graphics.lineTo(px, py);
    }
    graphics.closePath();
    if (fill) graphics.fillPath();
    else graphics.strokePath();
  }
}
